import { BaseService, type StandardEntityParams, type EntityListResponse, type NavigationFilters, type ServiceConfig } from './base-service';
import { FilteringUtils } from './utils/filtering.utils';
import { PAGINATION } from './constants/service.constants';

export abstract class MockService<TEntity, TParams extends StandardEntityParams>
  extends BaseService<TEntity, EntityListResponse<TEntity>, TParams> {

  protected abstract mockData: TEntity[];
  protected abstract readonly searchFields: string[];
  protected abstract readonly facetFields: string[];

  constructor(config: ServiceConfig) {
    super(config);
  }

  async getEntitiesForNavigation(
    page: number,
    filters: NavigationFilters
  ): Promise<EntityListResponse<TEntity>> {
    const pageSize = filters.pageSize || PAGINATION.DEFAULT_PAGE_SIZE;
    const params: TParams = {
      page: page - 1,
      pageSize,
      query: filters.query,
      status: filters.statusFilter,
    } as TParams;

    const filteredData = this.applyFilters(this.mockData, params);

    const allDataFacets = FilteringUtils.calculateFacets(this.mockData, this.facetFields);

    const paginatedResult = FilteringUtils.paginateData(
      filteredData,
      page - 1,
      pageSize
    );

    return {
      data: paginatedResult.data,
      total: filteredData.length,
      facets: allDataFacets,
      page: page - 1,
      pageSize
    };
  }

  /** Expose mock data for services that need direct mutation (create/update). */
  getMockData(): TEntity[] {
    return this.mockData;
  }

  async getEntityById(id: string): Promise<TEntity> {
    const entity = this.mockData.find((item) => (item as Record<string, unknown>).id === id);
    if (!entity) {
      throw this.createServiceError('fetch', new Error(`${this.entityName} ${id} not found`));
    }
    return entity;
  }

  protected applyFilters(data: TEntity[], params: TParams): TEntity[] {
    return FilteringUtils.applyFilters(data, params, {
      searchFields: this.searchFields,
      statusMappings: this.getStatusMappings(),
      customFilters: this.getCustomFilters()
    });
  }

  protected abstract getCustomFilters(): Record<string, (items: TEntity[], value: unknown) => TEntity[]>;
  protected abstract getStatusMappings(): Record<string, readonly string[]>;
}
